/**
 * Runs `linesToAutoMatch` and applies its plan. Renders nothing.
 *
 * ---- Why a component and not a hook ----
 *
 * `RfqPage`'s `applyItemToLine` is defined after the page's loading/error early-returns, so a
 * hook using it could not be declared unconditionally at the top of that component. A child
 * mounted only once the enquiry has loaded gets the same effect with its hooks in a place the
 * rules allow, and it keeps the auto-match state (which lines have been tried) out of the page.
 *
 * ---- Firing exactly once per line ----
 *
 * `attempted` is marked BEFORE the first `await`, not after. Three things would otherwise cause a
 * duplicate write: React's development double-invoke of effects, the re-render that follows each
 * write landing (`lines` changes, so the effect re-runs), and a write that FAILS — which would
 * otherwise be replanned on the next render, forever. Marking up front makes each line a
 * single attempt per session, whatever happens to it.
 *
 * Writes are SEQUENTIAL, matching `applyAllSuggestions`: `updateEnquiryItems` is a
 * read-modify-write over the whole items array, serialised per enquiry by `withEnquiryLock`, so
 * firing them together would queue anyway and interleaved reads risk losing an edit.
 *
 * ---- Why the callbacks are read through a ref ----
 *
 * `matchFor`, `apply` and `onDone` are re-created on every render of the page. Depending on them
 * directly would re-run this effect constantly; making the PAGE memoise them instead cost a
 * "Rendered more hooks than during the previous render" crash, because the only place those
 * `useCallback`s could see `applyItemToLine` was after `RfqPage`'s loading/error early-returns —
 * so the loading render ran three hooks fewer than the loaded one.
 *
 * Keeping the latest values in a ref removes the requirement instead of pushing it uphill: the
 * page passes plain inline functions, and the effect depends only on `lines` and `enabled`, which
 * are the things that should actually retrigger it.
 */

import { useEffect, useRef } from 'react'
import { linesToAutoMatch } from './autoMatch'
import type { EnquiryItem } from '../../lib/types'
import type { ItemMatch } from '../../lib/searchMatch'

export function AutoMatcher({
  lines,
  matchFor,
  applyAll,
  enabled,
  matches,
  onDone,
  unresolvableCodes,
}: {
  lines: EnquiryItem[]
  /**
   * Codes the item master has been asked for and does NOT have — the same fact `LineRow` renders
   * as "not in catalogue". A line carrying one is undecided, not decided; see `linesToAutoMatch`.
   * Must never include a code whose lookup is still in flight.
   */
  unresolvableCodes?: ReadonlySet<string>
  matchFor: (line: EnquiryItem) => ItemMatch | undefined
  /**
   * Commits the WHOLE plan — every line's lookups run concurrently and land in one
   * `update_enquiry`. Returns how many lines were actually written.
   *
   * Batched rather than called per line because `items` replaces wholesale: N lines cost the same
   * one round trip as one line. Per line it was ~3.4s each (two lookup rounds plus a
   * read-modify-write), so a seventeen-line enquiry spent ~58s filling itself in — slow enough
   * that rows sat visibly unmatched while the purchaser was already reading the table.
   */
  applyAll: (actions: Array<{ lineId: string; itemCode: string; score: number }>) => Promise<number>
  /** False until the matcher has answered at least one line, or while a bulk action owns the table. */
  enabled: boolean
  /**
   * The matches as they stand — a dependency, so a pass runs each time another line's answer
   * lands (2026-09-17: results arrive one line at a time now, not as one batch at the end).
   */
  matches: unknown
  /** Told what happened, once per batch, so the page can say so. */
  /**
   * Optional since 2026-09-17: the page no longer toasts the summary. The matcher answers
   * incrementally (`useMatchItems` per line), so one summary per batch stacked three or four
   * "Matched 1 item · 1 low-confidence" pop-ups on a single page load (Akhil: "too much pop for
   * these — don't bring any"). The table already says it on every row — the confidence bar and
   * the "low confidence" flag — so the toast repeated what was in front of the purchaser.
   */
  onDone?: (summary: { matched: number; lowConfidence: number }) => void
}) {
  const attempted = useRef<Set<string>>(new Set())
  const running = useRef(false)

  // Latest callbacks, without making them effect dependencies — see the header.
  const latest = useRef({ matchFor, applyAll, onDone })
  useEffect(() => {
    latest.current = { matchFor, applyAll, onDone }
  })

  useEffect(() => {
    if (!enabled || running.current) return
    const plan = linesToAutoMatch(lines, latest.current.matchFor, attempted.current, unresolvableCodes)
    if (plan.length === 0) return

    // Before the first await — see the header.
    for (const action of plan) attempted.current.add(action.lineId)
    running.current = true

    void (async () => {
      try {
        const matched = await latest.current.applyAll(
          plan.map(({ lineId, itemCode, score }) => ({ lineId, itemCode, score })),
        )
        if (matched > 0) {
          // Counted from the PLAN: the batch reports a total, not per-line results. Clamped
          // because a line whose lookups failed is dropped from the write but is still in the
          // plan — the flagged count must never exceed what was actually written.
          const lowConfidence = plan.filter((a) => a.lowConfidence).length
          latest.current.onDone?.({ matched, lowConfidence: Math.min(lowConfidence, matched) })
        }
      } catch {
        // The batch failed as a whole — every line stays unmatched and the purchaser matches by
        // hand, exactly as before this ran. The mutation reports the error itself.
      } finally {
        running.current = false
      }
    })()
    // Only what should retrigger a pass: the lines themselves, and whether it may run.
    // …and each answer that lands (2026-09-17: results arrive one line at a time now).
  }, [lines, enabled, unresolvableCodes, matches])

  return null
}
